import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Target, BarChart3, User, Bus, Train, Ship, Plane, ClipboardList, DoorOpen, Car, Flag, Calendar, Smartphone } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import UpcomingMatchesDashboard from '../components/sports/UpcomingMatchesDashboard';

/**
 * Voyageplanner V1 - B2B SaaS für Profisportvereine
 * Multimodale Spieltags-Logistik über Land, Wasser, Luft
 */
const HomeV1: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [dashboardView, setDashboardView] = useState<'landing' | 'matches'>('landing');

  // Tracking Events
  const trackEvent = (eventName: string, properties?: any) => {
    // Analytics tracking würde hier implementiert werden
    console.log(`Track: ${eventName}`, properties);
  };

  // If user is authenticated and wants to see matches dashboard
  if (isAuthenticated && dashboardView === 'matches') {
    return (
      <div className="page-container" style={containerStyle}>
        {/* Upcoming Matches Dashboard */}
        <UpcomingMatchesDashboard
          daysAhead={30}
          maxMatches={20}
          showHeader={true}
          onBackToLanding={() => setDashboardView('landing')}
        />
      </div>
    );
  }

  return (
    <div className="page-container" style={containerStyle}>
      {/* Hero Section */}
      <section style={heroStyle}>
        <div style={heroContentStyle}>
          <h1 style={heroTitleStyle}>
            Spieltage bewegen – Land. Wasser. Luft.
          </h1>
          <p style={heroSubtitleStyle}>
            Planen Sie Spiele, bauen Sie multimodale Routen und weisen Sie Fahrzeuge zu – 
            mit klaren Status-Meilensteinen von Abfahrt bis Ankunft.
          </p>
          
          <div style={heroButtonsStyle}>
            {!isAuthenticated ? (
              <>
                <Link
                  to="/matchday-planning-demo"
                  style={primaryButtonStyle}
                  onClick={() => trackEvent('hero_cta_demo_click')}
                >
                  <Target size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> Live Demo testen
                </Link>
                <Link
                  to="/demo"
                  style={secondaryButtonStyle}
                  onClick={() => trackEvent('hero_cta_demo_request_click')}
                >
                  Demo anfragen
                </Link>
                <Link
                  to="/tour"
                  style={{...secondaryButtonStyle, marginLeft: '1rem'}}
                  onClick={() => trackEvent('hero_cta_tour_click')}
                >
                  Produkt-Tour ansehen
                </Link>
              </>
            ) : (
              <>
                <button
                  onClick={() => setDashboardView('matches')}
                  style={primaryButtonStyle}
                >
                  <BarChart3 size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> Spiele-Dashboard
                </button>
                <Link
                  to="/matchday-planning"
                  style={secondaryButtonStyle}
                >
                  <Target size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> Spieltagsplanung
                </Link>
                <Link
                  to="/profile"
                  style={{...secondaryButtonStyle, marginLeft: '1rem'}}
                >
                  <User size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> Mein Profil
                </Link>
              </>
            )}
          </div>

          {/* Multimodal Icons */}
          <div style={modalIconsStyle}>
            <div style={iconItemStyle}>
              <div style={{...iconStyle, backgroundColor: '#3498db'}}><Bus size={24} color="white" /></div>
              <span style={iconLabelStyle}>Bus/Van</span>
            </div>
            <div style={iconItemStyle}>
              <div style={{...iconStyle, backgroundColor: '#27ae60'}}><Train size={24} color="white" /></div>
              <span style={iconLabelStyle}>Bahn</span>
            </div>
            <div style={iconItemStyle}>
              <div style={{...iconStyle, backgroundColor: '#2980b9'}}><Ship size={24} color="white" /></div>
              <span style={iconLabelStyle}>Fähre</span>
            </div>
            <div style={iconItemStyle}>
              <div style={{...iconStyle, backgroundColor: '#8e44ad'}}><Plane size={24} color="white" /></div>
              <span style={iconLabelStyle}>Flug</span>
            </div>
          </div>
        </div>
      </section>

      {/* Spieltage im Blick */}
      <section style={sectionStyle}>
        <div style={containerInnerStyle}>
          <h2 style={sectionTitleStyle}>Spielekalender zentral verwalten</h2>
          <p style={sectionTextStyle}>
            Importieren Sie Spielpläne, filtern Sie nach Teams und Wettbewerben, und behalten Sie alle Anstoß- und Treffzeiten im Überblick. Heim- und Auswärtsspiele werden automatisch erkannt.
          </p>
          <div style={bulletListStyle}>
            <div style={bulletItemStyle}>✓ Heim/Auswärts automatisch erkennen</div>
            <div style={bulletItemStyle}>✓ Mehrere Teams & Wettbewerbe verwalten</div>
            <div style={bulletItemStyle}>✓ CSV-Import & Kalender-Sync</div>
          </div>
          <Link 
            to="/calendar-demo" 
            style={sectionCtaStyle}
            onClick={() => trackEvent('calendar_import_click')}
          >
            Kalender-Demo ansehen
          </Link>
        </div>
      </section>

      {/* Leg-Builder & Disposition */}
      <section style={{...sectionStyle, backgroundColor: '#f8f9fa'}}>
        <div style={containerInnerStyle}>
          <h2 style={sectionTitleStyle}>Multimodale Routen in Segmenten</h2>
          <p style={sectionTextStyle}>
            Bauen Sie komplexe Wege: Bus → Flug → Bus oder Bus → Fähre → Bus. Weisen Sie Fahrzeuge zu, verwalten Sie Kapazitäten und planen Sie Umstiege mit Pufferzeiten.
          </p>
          
          {/* Route Example */}
          <div style={routeExampleStyle}>
            <div style={routeStepStyle}>
              <div style={{...iconStyle, backgroundColor: '#3498db', fontSize: '1.5rem'}}><Bus size={20} color="white" /></div>
              <span>Bus</span>
            </div>
            <div style={routeArrowStyle}>→</div>
            <div style={routeStepStyle}>
              <div style={{...iconStyle, backgroundColor: '#8e44ad', fontSize: '1.5rem'}}><Plane size={20} color="white" /></div>
              <span>Flug</span>
            </div>
            <div style={routeArrowStyle}>→</div>
            <div style={routeStepStyle}>
              <div style={{...iconStyle, backgroundColor: '#3498db', fontSize: '1.5rem'}}><Bus size={20} color="white" /></div>
              <span>Bus</span>
            </div>
          </div>

          <div style={bulletListStyle}>
            <div style={bulletItemStyle}>✓ Umstiege mit Pufferzeiten planen</div>
            <div style={bulletItemStyle}>✓ Kapazität & Headcount verwalten</div>
            <div style={bulletItemStyle}>✓ Team/Staff getrennt organisieren</div>
          </div>
          <Link 
            to="/leg-builder-demo" 
            style={sectionCtaStyle}
            onClick={() => trackEvent('leg_builder_view')}
          >
            Leg-Builder testen
          </Link>
        </div>
      </section>

      {/* Status & Tracking */}
      <section style={sectionStyle}>
        <div style={containerInnerStyle}>
          <h2 style={sectionTitleStyle}>Status-Meilensteine verfolgen</h2>
          <p style={sectionTextStyle}>
            Von geplant über boarding bis angekommen – behalten Sie den Überblick und informieren Sie automatisch bei Verspätungen. Jeder Spieltag hat seine eigene Timeline.
          </p>
          
          {/* Status Timeline */}
          <div style={timelineStyle}>
            <div style={timelineItemStyle}>
              <div style={{...timelineIconStyle, backgroundColor: '#95a5a6'}}><ClipboardList size={18} color="white" /></div>
              <span>Geplant</span>
            </div>
            <div style={timelineArrowStyle}>→</div>
            <div style={timelineItemStyle}>
              <div style={{...timelineIconStyle, backgroundColor: '#f39c12'}}><DoorOpen size={18} color="white" /></div>
              <span>Boarding</span>
            </div>
            <div style={timelineArrowStyle}>→</div>
            <div style={timelineItemStyle}>
              <div style={{...timelineIconStyle, backgroundColor: '#3498db'}}><Car size={18} color="white" /></div>
              <span>Unterwegs</span>
            </div>
            <div style={timelineArrowStyle}>→</div>
            <div style={timelineItemStyle}>
              <div style={{...timelineIconStyle, backgroundColor: '#27ae60'}}><Flag size={18} color="white" /></div>
              <span>Angekommen</span>
            </div>
          </div>

          <div style={bulletListStyle}>
            <div style={bulletItemStyle}>✓ Push-Benachrichtigungen bei Änderungen</div>
            <div style={bulletItemStyle}>✓ ETA-Updates in Echtzeit</div>
            <div style={bulletItemStyle}>✓ Timeline je Spiel verfügbar</div>
          </div>
          <Link 
            to="/status-demo" 
            style={sectionCtaStyle}
            onClick={() => trackEvent('status_update_click')}
          >
            Status-Demo ansehen
          </Link>
        </div>
      </section>

      {/* Verkehrsmittel-Übersicht */}
      <section style={{...sectionStyle, backgroundColor: '#f8f9fa'}}>
        <div style={containerInnerStyle}>
          <h2 style={sectionTitleStyle}>Verkehrsmittel für jeden Spieltag</h2>
          <div style={vehicleGridStyle}>
            <div style={vehicleCardStyle}>
              <div style={{...iconStyle, backgroundColor: '#3498db', fontSize: '2rem'}}><Bus size={28} color="white" /></div>
              <h3 style={vehicleCardTitleStyle}>Bus/Van</h3>
              <p style={vehicleCardTextStyle}>Flexibel für kurze Strecken und Direktverbindungen zum Stadion</p>
            </div>
            <div style={vehicleCardStyle}>
              <div style={{...iconStyle, backgroundColor: '#27ae60', fontSize: '2rem'}}><Train size={28} color="white" /></div>
              <h3 style={vehicleCardTitleStyle}>Bahn</h3>
              <p style={vehicleCardTextStyle}>Umweltfreundlich für Langstrecken zwischen Großstädten</p>
            </div>
            <div style={vehicleCardStyle}>
              <div style={{...iconStyle, backgroundColor: '#2980b9', fontSize: '2rem'}}><Ship size={28} color="white" /></div>
              <h3 style={vehicleCardTitleStyle}>Fähre/Schiff</h3>
              <p style={vehicleCardTextStyle}>Überquerung von Gewässern mit Equipment-Transport</p>
            </div>
            <div style={vehicleCardStyle}>
              <div style={{...iconStyle, backgroundColor: '#8e44ad', fontSize: '2rem'}}><Plane size={28} color="white" /></div>
              <h3 style={vehicleCardTitleStyle}>Flug/Charter</h3>
              <p style={vehicleCardTextStyle}>Schnelle Verbindungen für internationale Spiele</p>
            </div>
          </div>
        </div>
      </section>

      {/* Integrationen & Roadmap */}
      <section style={sectionStyle}>
        <div style={containerInnerStyle}>
          <h2 style={sectionTitleStyle}>Einfache Anbindungen</h2>
          <p style={sectionTextStyle}>
            Kalender-Sync, CSV-Roster-Import und mobile Updates – ohne komplexe IT-Projekte. Nutzen Sie bestehende Systeme weiter.
          </p>
          <div style={integrationGridStyle}>
            <div style={integrationCardStyle}>
              <div style={{...iconStyle, backgroundColor: '#27ae60', fontSize: '2rem'}}><Calendar size={28} color="white" /></div>
              <h3 style={vehicleCardTitleStyle}>Kalender-Sync</h3>
              <p style={vehicleCardTextStyle}>Bestehende Kalender nutzen</p>
            </div>
            <div style={integrationCardStyle}>
              <div style={{...iconStyle, backgroundColor: '#f39c12', fontSize: '2rem'}}><BarChart3 size={28} color="white" /></div>
              <h3 style={vehicleCardTitleStyle}>CSV-Import</h3>
              <p style={vehicleCardTextStyle}>Team-Roster importieren</p>
            </div>
            <div style={integrationCardStyle}>
              <div style={{...iconStyle, backgroundColor: '#9b59b6', fontSize: '2rem'}}><Smartphone size={28} color="white" /></div>
              <h3 style={vehicleCardTitleStyle}>Mobile-First</h3>
              <p style={vehicleCardTextStyle}>Optimiert für unterwegs</p>
            </div>
          </div>
          <div style={roadmapTeaserStyle}>
            <p style={roadmapTextStyle}>
              <strong>Roadmap V2:</strong> GPS-Tracking, SSO und Budget-Module folgen
            </p>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section style={{...sectionStyle, backgroundColor: '#2c3e50', color: 'white'}}>
        <div style={containerInnerStyle}>
          <div style={socialProofGridStyle}>
            <div style={metricItemStyle}>
              <div style={metricNumberStyle}>-40%</div>
              <div style={metricLabelStyle}>Koordinationsaufwand</div>
            </div>
            <div style={metricItemStyle}>
              <div style={metricNumberStyle}>+25%</div>
              <div style={metricLabelStyle}>Pünktlichkeit</div>
            </div>
            <div style={metricItemStyle}>
              <div style={metricNumberStyle}>50+</div>
              <div style={metricLabelStyle}>Vereine</div>
            </div>
          </div>
          <div style={testimonialStyle}>
            <p style={testimonialTextStyle}>
              "Endlich haben wir alle Spieltage im Griff – von der Planung bis zur Ankunft. Die multimodalen Routen sparen uns Stunden an Koordination."
            </p>
            <div style={testimonialAuthorStyle}>
              <strong>Thomas M.</strong>, Team Operations, Bundesliga-Verein
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section style={bottomCtaStyle}>
        <div style={containerInnerStyle}>
          <h2 style={bottomCtaTitleStyle}>Spieltage sicher orchestrieren – über Land, Wasser und Luft</h2>
          <p style={bottomCtaTextStyle}>
            Starten Sie mit einer Demo und erleben Sie, wie einfach multimodale Spieltags-Logistik sein kann.
          </p>
          <div style={heroButtonsStyle}>
            <Link 
              to="/demo" 
              style={primaryButtonStyle}
              onClick={() => trackEvent('bottom_cta_demo_click')}
            >
              Demo anfragen
            </Link>
            <Link 
              to="/tour" 
              style={{...secondaryButtonStyle, borderColor: '#e74c3c', color: '#e74c3c'}}
              onClick={() => trackEvent('bottom_cta_tour_click')}
            >
              Produkt-Tour ansehen
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// Styles
const containerStyle: React.CSSProperties = {
  minHeight: '100vh',
  backgroundColor: '#ffffff',
};

const heroStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  padding: '6rem 2rem',
  textAlign: 'center',
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
};

const heroContentStyle: React.CSSProperties = {
  maxWidth: '1000px',
  margin: '0 auto',
  position: 'relative',
  zIndex: 2,
};

const heroTitleStyle: React.CSSProperties = {
  fontSize: '3.5rem',
  fontWeight: 'bold',
  marginBottom: '1.5rem',
  lineHeight: '1.2',
  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
};

const heroSubtitleStyle: React.CSSProperties = {
  fontSize: '1.3rem',
  marginBottom: '2.5rem',
  lineHeight: '1.6',
  opacity: 0.95,
  maxWidth: '800px',
  margin: '0 auto 2.5rem',
};

const heroButtonsStyle: React.CSSProperties = {
  display: 'flex',
  gap: '1rem',
  justifyContent: 'center',
  flexWrap: 'wrap',
  marginBottom: '3rem',
};

const primaryButtonStyle: React.CSSProperties = {
  backgroundColor: '#e74c3c',
  color: 'white',
  padding: '1rem 2rem',
  borderRadius: '8px',
  textDecoration: 'none',
  fontSize: '1.1rem',
  fontWeight: '600',
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 15px rgba(231, 76, 60, 0.3)',
  border: '2px solid transparent',
};

const secondaryButtonStyle: React.CSSProperties = {
  backgroundColor: 'transparent',
  color: 'white',
  padding: '1rem 2rem',
  borderRadius: '8px',
  textDecoration: 'none',
  fontSize: '1.1rem',
  fontWeight: '600',
  transition: 'all 0.3s ease',
  border: '2px solid white',
};

const modalIconsStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  gap: '2rem',
  flexWrap: 'wrap',
  marginTop: '2rem',
};

const iconItemStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.5rem',
};

const iconStyle: React.CSSProperties = {
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '1.8rem',
  color: 'white',
  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
};

const iconLabelStyle: React.CSSProperties = {
  fontSize: '0.9rem',
  fontWeight: '500',
  opacity: 0.9,
};

const sectionStyle: React.CSSProperties = {
  padding: '5rem 2rem',
  backgroundColor: 'white',
};

const containerInnerStyle: React.CSSProperties = {
  maxWidth: '1000px',
  margin: '0 auto',
  textAlign: 'center',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: '2.5rem',
  fontWeight: 'bold',
  marginBottom: '1.5rem',
  color: '#2c3e50',
};

const sectionTextStyle: React.CSSProperties = {
  fontSize: '1.2rem',
  lineHeight: '1.6',
  color: '#7f8c8d',
  marginBottom: '2rem',
  maxWidth: '800px',
  margin: '0 auto 2rem',
};

const bulletListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.8rem',
  marginBottom: '2rem',
  textAlign: 'left',
  maxWidth: '600px',
  margin: '0 auto 2rem',
};

const bulletItemStyle: React.CSSProperties = {
  fontSize: '1.1rem',
  color: '#2c3e50',
  fontWeight: '500',
};

const sectionCtaStyle: React.CSSProperties = {
  display: 'inline-block',
  backgroundColor: '#3498db',
  color: 'white',
  padding: '0.8rem 1.5rem',
  borderRadius: '6px',
  textDecoration: 'none',
  fontSize: '1rem',
  fontWeight: '600',
  transition: 'all 0.3s ease',
};

const routeExampleStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '1rem',
  marginBottom: '2rem',
  flexWrap: 'wrap',
};

const routeStepStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.5rem',
};

const routeArrowStyle: React.CSSProperties = {
  fontSize: '1.5rem',
  color: '#3498db',
  fontWeight: 'bold',
};

const timelineStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '1rem',
  marginBottom: '2rem',
  flexWrap: 'wrap',
};

const timelineItemStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.5rem',
};

const timelineIconStyle: React.CSSProperties = {
  width: '50px',
  height: '50px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '1.2rem',
  color: 'white',
};

const timelineArrowStyle: React.CSSProperties = {
  fontSize: '1.2rem',
  color: '#95a5a6',
  fontWeight: 'bold',
};

const vehicleGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: '2rem',
  marginTop: '2rem',
};

const vehicleCardStyle: React.CSSProperties = {
  backgroundColor: 'white',
  padding: '2rem',
  borderRadius: '12px',
  textAlign: 'center',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  transition: 'transform 0.3s ease',
};

const vehicleCardTitleStyle: React.CSSProperties = {
  fontSize: '1.3rem',
  fontWeight: '600',
  margin: '1rem 0 0.5rem',
  color: '#2c3e50',
};

const vehicleCardTextStyle: React.CSSProperties = {
  color: '#7f8c8d',
  lineHeight: '1.5',
};

const integrationGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '2rem',
  marginTop: '2rem',
  marginBottom: '2rem',
};

const integrationCardStyle: React.CSSProperties = {
  backgroundColor: '#f8f9fa',
  padding: '1.5rem',
  borderRadius: '8px',
  textAlign: 'center',
  border: '2px solid #e9ecef',
};

const roadmapTeaserStyle: React.CSSProperties = {
  backgroundColor: '#e8f4fd',
  padding: '1.5rem',
  borderRadius: '8px',
  border: '1px solid #bee5eb',
  marginTop: '2rem',
};

const roadmapTextStyle: React.CSSProperties = {
  color: '#0c5460',
  fontSize: '1.1rem',
  margin: 0,
};

const socialProofGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '2rem',
  marginBottom: '3rem',
  textAlign: 'center',
};

const metricItemStyle: React.CSSProperties = {
  padding: '1rem',
};

const metricNumberStyle: React.CSSProperties = {
  fontSize: '3rem',
  fontWeight: 'bold',
  color: '#e74c3c',
  marginBottom: '0.5rem',
};

const metricLabelStyle: React.CSSProperties = {
  fontSize: '1.1rem',
  opacity: 0.9,
};

const testimonialStyle: React.CSSProperties = {
  maxWidth: '700px',
  margin: '0 auto',
  textAlign: 'center',
};

const testimonialTextStyle: React.CSSProperties = {
  fontSize: '1.3rem',
  fontStyle: 'italic',
  lineHeight: '1.6',
  marginBottom: '1rem',
  opacity: 0.95,
};

const testimonialAuthorStyle: React.CSSProperties = {
  fontSize: '1rem',
  opacity: 0.8,
};

const bottomCtaStyle: React.CSSProperties = {
  padding: '5rem 2rem',
  backgroundColor: '#f8f9fa',
  textAlign: 'center',
};

const bottomCtaTitleStyle: React.CSSProperties = {
  fontSize: '2.2rem',
  fontWeight: 'bold',
  marginBottom: '1rem',
  color: '#2c3e50',
};

const bottomCtaTextStyle: React.CSSProperties = {
  fontSize: '1.2rem',
  color: '#7f8c8d',
  marginBottom: '2rem',
  maxWidth: '600px',
  margin: '0 auto 2rem',
  lineHeight: '1.6',
};



export default HomeV1;
